import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ErrorCode,
  contactNextStepKinds,
  contactNextStepLabels,
  type CreateContactBody,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Dialog,
  FieldGroup,
  RadioCard,
  Select,
  TextField,
  TextLink,
} from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { createContact, useContactReferrers, useContactSources, useDuplicateCheck } from './api';
import type { CreatedContact, DuplicateHit } from './types';
import styles from './dialogs.module.css';

const formSchema = z
  .object({
    firstName: z.string().trim().min(1, 'Pflichtangabe.'),
    lastName: z.string().trim().min(1, 'Pflichtangabe.'),
    phone: z.string().trim(),
    email: z.string().trim(),
    sourceChannel: z.string().min(1, 'Quelle ist Pflicht für die Auswertung.'),
    referrerId: z.string(),
    nextStepKind: z.enum(contactNextStepKinds),
    taskTitle: z.string().trim(),
    taskDueOn: z.string(),
    taskDueTime: z.string(),
  })
  .superRefine((value, ctx) => {
    if (!value.phone && !value.email) {
      ctx.addIssue({ code: 'custom', path: ['phone'], message: 'Telefon oder E-Mail angeben.' });
    }
    if (value.nextStepKind === 'task_for_me' && !value.taskDueOn) {
      ctx.addIssue({
        code: 'custom',
        path: ['taskDueOn'],
        message: 'Bitte ein Datum im Format JJJJ-MM-TT angeben.',
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

const SERVER_FIELDS: Record<string, FieldPath<FormValues>> = {
  firstName: 'firstName',
  lastName: 'lastName',
  phone: 'phone',
  email: 'email',
  sourceChannel: 'sourceChannel',
  referrerId: 'referrerId',
  'nextStep.dueOn': 'taskDueOn',
  'nextStep.dueTime': 'taskDueTime',
  'nextStep.title': 'taskTitle',
};

const FIELD_ORDER: FieldPath<FormValues>[] = [
  'firstName',
  'lastName',
  'phone',
  'email',
  'sourceChannel',
  'referrerId',
  'nextStepKind',
  'taskTitle',
  'taskDueOn',
  'taskDueTime',
];

/** "1 Angabe fehlt" / "2 Angaben fehlen" (Kontakte 05 footer). */
function missingText(count: number): string {
  return count === 1 ? '1 Angabe fehlt' : `${count} Angaben fehlen`;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export interface CreateContactDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (contact: CreatedContact) => void;
  /** Opens the existing record of a duplicate hit ("Vorhandenen öffnen"). */
  onOpenExisting: (id: string) => void;
}

/** Kontakte 04/05 · „Kontakt anlegen": the create form with the live duplicate warning and field errors. */
export function CreateContactDialog({ open, onClose, onCreated, onOpenExisting }: CreateContactDialogProps) {
  const sources = useContactSources(open);
  const referrers = useContactReferrers(open);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      sourceChannel: '',
      referrerId: '',
      nextStepKind: 'dialer_pool',
      taskTitle: '',
      taskDueOn: '',
      taskDueTime: '',
    },
  });
  const { register, handleSubmit, watch, setError, setFocus, reset, formState } = form;
  const [serverHits, setServerHits] = useState<{ message: string; duplicates: DuplicateHit[] } | null>(null);
  const [despite, setDespite] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    reset();
    setServerHits(null);
    setDespite(false);
    setBanner(null);
  }, [open, reset]);

  const phone = watch('phone');
  const email = watch('email');
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const nextStepKind = watch('nextStepKind');
  const checkParams = useDebounced(
    useMemo(
      () => ({
        phone: phone.trim(),
        email: email.trim(),
        firstName: firstName.trim() && lastName.trim() ? firstName.trim() : '',
        lastName: firstName.trim() && lastName.trim() ? lastName.trim() : '',
      }),
      [phone, email, firstName, lastName],
    ),
    400,
  );
  const liveCheck = useDuplicateCheck(checkParams, open);
  const hits = serverHits ?? (liveCheck.data && liveCheck.data.duplicates.length > 0 ? liveCheck.data : null);
  const hit = hits?.duplicates[0] ?? null;
  const hitMessage = hits?.message ?? 'Möglicher Doppeleintrag';
  const errorCount = Object.keys(formState.errors).length;

  // `despite` is read through a ref: „Trotzdem neu anlegen" sets it and submits in the same tick.
  const despiteRef = useRef(false);
  despiteRef.current = despite;

  const submit = handleSubmit(async (values) => {
    const despiteNow = despiteRef.current;
    setBanner(null);
    const body: CreateContactBody = {
      firstName: values.firstName,
      lastName: values.lastName,
      sourceChannel: values.sourceChannel,
      referrerId: values.referrerId || null,
      nextStep:
        values.nextStepKind === 'task_for_me'
          ? {
              kind: 'task_for_me',
              dueOn: values.taskDueOn,
              ...(values.taskTitle ? { title: values.taskTitle } : {}),
              ...(values.taskDueTime ? { dueTime: values.taskDueTime } : {}),
            }
          : { kind: values.nextStepKind },
      ...(values.phone ? { phone: values.phone } : {}),
      ...(values.email ? { email: values.email } : {}),
      ...(despiteNow ? { createDespiteDuplicate: true } : {}),
      ...(despiteNow && hit ? { duplicateOfId: hit.id } : {}),
    };
    setPending(true);
    try {
      const created = await createContact(body);
      onCreated(created);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        const fields = error.fields;
        let first: FieldPath<FormValues> | null = null;
        for (const key of FIELD_ORDER) {
          const serverKey = Object.entries(SERVER_FIELDS).find(([, field]) => field === key)?.[0];
          const message = serverKey ? fields[serverKey] : undefined;
          if (message) {
            setError(key, { type: 'server', message });
            first ??= key;
          }
        }
        if (first) setFocus(first);
        else setBanner(error.message);
      } else if (error instanceof ApiError && error.code === ErrorCode.CONFLICT) {
        const duplicates = error.details?.['duplicates'];
        setServerHits({
          message: error.message,
          duplicates: Array.isArray(duplicates) ? (duplicates as DuplicateHit[]) : [],
        });
        if (!Array.isArray(duplicates) || duplicates.length === 0) setBanner(error.message);
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  });

  const formId = 'create-contact-form';
  const footerNote =
    errorCount > 0
      ? missingText(errorCount)
      : hit
        ? 'Legen Sie trotzdem an, bekommt der Admin einen Hinweis.'
        : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Kontakte"
      title="Kontakt anlegen"
      width={600}
      closeOnScrim={!formState.isDirty}
      footerNote={footerNote}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div className={styles.twoColumns}>
          <TextField
            label="Vorname"
            required
            autoComplete="off"
            autoFocus
            error={formState.errors.firstName?.message}
            {...register('firstName')}
          />
          <TextField
            label="Nachname"
            required
            autoComplete="off"
            error={formState.errors.lastName?.message}
            {...register('lastName')}
          />
        </div>
        <div className={styles.twoColumns}>
          <TextField
            label="Telefon"
            type="tel"
            required
            autoComplete="off"
            error={formState.errors.phone?.message}
            {...register('phone')}
          />
          <TextField
            label="E-Mail"
            type="email"
            autoComplete="off"
            error={formState.errors.email?.message}
            invalid={Boolean(formState.errors.phone)}
            {...register('email')}
          />
        </div>
        {hit ? (
          <Banner
            tone="warning"
            title={hitMessage}
            actions={
              <>
                <Button variant="secondary" size="small" onClick={() => onOpenExisting(hit.id)}>
                  Vorhandenen öffnen
                </Button>
                <TextLink
                  as="button"
                  onClick={() => {
                    setDespite(true);
                    despiteRef.current = true;
                    void submit();
                  }}
                >
                  Trotzdem neu anlegen
                </TextLink>
              </>
            }
          >
            <span className={styles.hitLine}>
              <span className={styles.hitName}>{hit.name}</span>
              {hit.phone ? ` · ${hit.phone}` : ''}
              {!hit.phone && hit.email ? ` · ${hit.email}` : ''} · {hit.lifecycleLabel}
              {hit.ownerName ? ` · Besitzer ${hit.ownerName}` : ''}
            </span>
          </Banner>
        ) : null}
        <div className={styles.twoColumns}>
          <Select
            label="Quelle"
            required
            placeholder="Bitte wählen"
            error={formState.errors.sourceChannel?.message}
            options={(sources.data?.sources ?? []).map((source) => ({ value: source, label: source }))}
            {...register('sourceChannel')}
          />
          <Select
            label="Tippgeber"
            optional
            placeholder="Keiner"
            error={formState.errors.referrerId?.message}
            options={(referrers.data?.referrers ?? []).map((user) => ({ value: user.id, label: user.name }))}
            {...register('referrerId')}
          />
        </div>
        <FieldGroup legend="Nächster Schritt" required columns={3} gap={8}>
          {contactNextStepKinds.map((kind) => (
            <RadioCard
              key={kind}
              value={kind}
              title={contactNextStepLabels[kind].title}
              description={contactNextStepLabels[kind].subtext}
              {...register('nextStepKind')}
            />
          ))}
        </FieldGroup>
        {nextStepKind === 'task_for_me' ? (
          <div className={styles.threeColumns}>
            <TextField
              label="Datum"
              type="date"
              required
              error={formState.errors.taskDueOn?.message}
              {...register('taskDueOn')}
            />
            <TextField
              label="Uhrzeit"
              type="time"
              error={formState.errors.taskDueTime?.message}
              {...register('taskDueTime')}
            />
            <TextField
              label="Titel"
              optional
              error={formState.errors.taskTitle?.message}
              {...register('taskTitle')}
            />
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}
